import { apiClient } from './apiClient';

export type ChatHandoffStatus = 'ACTIVE' | 'RETURNED';

export interface ChatTranscriptEntry {
  id: string;
  senderId: string;
  senderName?: string;
  type: string;
  content: string;
  payload?: any;
  createdAt: number;
}

export interface ChatUserSummary {
  userId: string;
  displayName?: string;
  username?: string;
  avatarUrl?: string;
  isOfficial?: boolean;
}

export interface ChatHandoff {
  handoffId: string;
  creatorUserId: string;
  clientUserId: string;
  officialUserId: string;
  sourceConversationId: string;
  officialConversationId: string;
  status: ChatHandoffStatus;
  transcriptMessageCount: number;
  createdAt: number;
  updatedAt: number;
  returnedAt?: number;
  returnNote?: string;
  creator?: ChatUserSummary;
  client?: ChatUserSummary;
  official?: ChatUserSummary;
}

export const chatApiService = {
  async getUserSig(): Promise<{ userSig: string; sdkAppId: number; userId: string }> {
    const res = await apiClient.get<{ success: boolean; data: { userSig: string; sdkAppId: number; userId: string } }>('/api/v1/chat/usersig');
    if (res.success && res.data?.data) return res.data.data;
    throw new Error(res.error || 'Failed to get UserSig');
  },

  async getOfficialInfo(): Promise<{ officialUser: ChatUserSummary; isCurrentUserOfficial: boolean }> {
    const res = await apiClient.get<{ success: boolean; data: { officialUser: ChatUserSummary; isCurrentUserOfficial: boolean } }>('/api/v1/chat/official');
    if (res.success && res.data?.data) return res.data.data;
    throw new Error(res.error || 'Failed to get official info');
  },

  async listHandoffs(): Promise<ChatHandoff[]> {
    const res = await apiClient.get<{ success: boolean; data: { handoffs: ChatHandoff[] } }>('/api/v1/chat/handoffs');
    if (res.success && res.data?.data) return res.data.data.handoffs || [];
    throw new Error(res.error || 'Failed to get chat handoffs');
  },

  async createHandoff(input: {
    clientUserId: string;
    sourceConversationId: string;
    transcript: ChatTranscriptEntry[];
  }): Promise<ChatHandoff> {
    const res = await apiClient.post<{ success: boolean; data: { handoff: ChatHandoff } }>('/api/v1/chat/handoffs', input);
    if (res.success && res.data?.data?.handoff) return res.data.data.handoff;
    throw new Error(res.error || 'Failed to create chat handoff');
  },

  async getHandoffTranscript(handoffId: string): Promise<ChatTranscriptEntry[]> {
    const res = await apiClient.get<{ success: boolean; data: { transcript: ChatTranscriptEntry[] } }>(`/api/v1/chat/handoffs/${handoffId}/transcript`);
    if (res.success && res.data?.data) return res.data.data.transcript || [];
    throw new Error(res.error || 'Failed to get chat handoff transcript');
  },

  async returnHandoff(handoffId: string, note?: string): Promise<ChatHandoff> {
    const res = await apiClient.post<{ success: boolean; data: { handoff: ChatHandoff } }>(`/api/v1/chat/handoffs/${handoffId}/return`, {
      note: note || ''
    });
    if (res.success && res.data?.data?.handoff) return res.data.data.handoff;
    throw new Error(res.error || 'Failed to return chat handoff');
  },

  async sendSmsReminder(partnerUserId: string): Promise<{ success: boolean; error?: string }> {
    const res = await apiClient.post<{ success: boolean; error?: string }>(
      '/api/v1/chat/sms-reminder',
      { partnerUserId }
    );
    if (res.success && res.data?.success) return { success: true };
    // Prefer the server-side error from the response body so the UI
    // can display the Chinese message we set on the route.
    const serverError = res.data?.error;
    return { success: false, error: serverError || res.error || '短信发送失败' };
  },
};
